import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { CiEdit } from "react-icons/ci";
import { RiForbid2Line } from "react-icons/ri";
import { CurrencyAction } from "../../../Redux/action/CurrencyAction";
import { CandidateProfile } from "../../../Redux/action/CandidateProfileAction";
import { useCurrency } from "../../../Hooks/useCommon";

const PRIMARY_COLOR = "#286a94";

// Format helpers
const formatCurrency = (val) =>
  val ? val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";

const unformatCurrency = (val) => val?.toString().replace(/,/g, "") || "";

const Pricing = ({ Id }) => {
  const dispatch = useDispatch();
  const { userResult } = useSelector((state) => state.Auth);
  const userInfo = useSelector((state) => state?.Profile?.result);
  const userData = userInfo?.candidatePrice;
  const { data: currency_data } = useCurrency();

  const candidateId = Id ?? userResult?.userid;

  const [isDisabled, setIsDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [autoCalculate, setAutoCalculate] = useState(true);
  const [suggestedSalary, setSuggestedSalary] = useState(null);

  const [data, setData] = useState({
    perHour: "",
    perDay: "",
    perWeek: "",
    perMonth: "",
    openForNegotiation: false,
    currency: "INR",
    ctc: "",
    // minExpected: "",
    // maxExpected: "",
    workPreference: "",
    noticePeriod: ""
  });

  useEffect(() => {
    setData(userData ? { ...data, ...userData } : { ...data });
  }, [userData]);

  // ⭐ AI Salary Analysis (Based on experience + skills + role)
  const getAISuggestion = async () => {
    if (!userInfo) return null;

    const exp = Number(userInfo?.keyExperience?.replace(/\D+/g, "")) || 0;
    const skills = userInfo?.skillList || [];
    const role = (userInfo?.currentDesignation || "").toLowerCase();

    let base = 45000;

    if (exp <= 1) base = 50000;
    else if (exp <= 3) base = 85000;
    else if (exp <= 6) base = 140000;
    else if (exp <= 10) base = 190000;
    else base = 260000;

    const premiumSkills = {
      react: 1.2,
      "react js": 1.2,
      angular: 1.15,
      node: 1.28,
      java: 1.22,
      python: 1.3,
      aws: 1.35,
      devops: 1.45,
      flutter: 1.23,
      "machine learning": 1.6,
      "data engineer": 1.55,
      ai: 1.8,
      blockchain: 1.95,
    };

    skills.forEach((skill) => {
      const key = skill.toLowerCase();
      if (premiumSkills[key]) base *= premiumSkills[key];
    });

    if (role.includes("architect") || role.includes("lead")) base *= 1.28;
    if (role.includes("senior")) base *= 1.18;

    return Math.round(base);
  };

  useEffect(() => {
    if (data.perMonth && autoCalculate) {
      getAISuggestion().then((salary) => salary && setSuggestedSalary(salary));
    }
  }, [data.perMonth, autoCalculate]);

  const validateSalary = () => {
    const m = Number(unformatCurrency(data.perMonth));
    const w = Number(unformatCurrency(data.perWeek));
    const d = Number(unformatCurrency(data.perDay));
    const h = Number(unformatCurrency(data.perHour));

    if (w && m && w >= m) return "Per week must be less than per month.";
    if (d && w && d >= w) return "Per day must be less than per week.";
    if (h && d && h >= d) return "Per hour must be less than per day.";
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const nonNumericFields = [
      "currency",
      "workPreference",
      "noticePeriod",
      "billingModel",
    ];

    if (nonNumericFields.includes(name)) {
      setData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    let clean = unformatCurrency(value);

    if (clean && !/^\d+$/.test(clean)) return;

    setData((prev) => {
      let updated = { ...prev, [name]: clean };

      if (name === "perMonth" && autoCalculate && clean) {
        const month = Number(clean);

        updated.perWeek = Math.round(month / 4).toString();
        updated.perDay = Math.round(month / 20).toString();
        updated.perHour = Math.round(updated.perDay / 8).toString();

        updated.ctc = (month * 12).toString();

        // updated.minExpected = Math.round(month * 0.9).toString();
        // updated.maxExpected = Math.round(month * 1.5).toString();
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validateSalary();
    if (msg) return alert(msg);

    setLoading(true);
    try {
      await dispatch(CurrencyAction({ ...data, candidateId }));
      await dispatch(CandidateProfile(candidateId));
      setIsDisabled(true);
    } finally {
      setLoading(false);
    }
  };

  const fieldStyles = {
    "& label.Mui-focused": { color: PRIMARY_COLOR },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: PRIMARY_COLOR + "40" },
      "&:hover fieldset": { borderColor: PRIMARY_COLOR },
      "&.Mui-focused fieldset": { borderColor: PRIMARY_COLOR, borderWidth: 2 },
    },
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6" color={PRIMARY_COLOR} fontWeight={600}>
            Salary & Billing Details
          </Typography>

          <Typography variant="caption" sx={{ color: "red", fontWeight: 600 }}>
            Fields marked with * are required
          </Typography>

          <Tooltip title={isDisabled ? "Enable Editing" : "Disable Editing"}>
            <span>
              <IconButton
                onClick={() => !loading && setIsDisabled((prev) => !prev)}
                disabled={loading}
              >
                {isDisabled ? (
                  <CiEdit size={22} color={PRIMARY_COLOR} />
                ) : (
                  <RiForbid2Line size={22} color="red" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Auto Toggle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Switch
            checked={autoCalculate}
            disabled={isDisabled}
            onChange={() => setAutoCalculate((p) => !p)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#286a94",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#286a94",
              },
            }}
          />

          <Typography sx={{ fontSize: 14, color: "#286a94", fontWeight: 500 }}>
            Auto Calculate From Monthly Salary
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          {/* BASIC COMPENSATION */}
          <Typography
            variant="subtitle2"
            sx={{ color: PRIMARY_COLOR, fontWeight: 600, mb: 1 }}
          >
            Basic Compensation
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={isDisabled} sx={fieldStyles}>
                <InputLabel>Currency</InputLabel>
                <Select
                  label="Currency"
                  name="currency"
                  value={data.currency}
                  onChange={handleChange}
                >
                  {currency_data?.map((item, i) => (
                    <MenuItem key={i} value={item.code}>
                      {item.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Current Annual CTC"
                name="ctc"
                disabled={isDisabled}
                value={formatCurrency(data.ctc)}
                onChange={handleChange}
                sx={fieldStyles}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* EXPECTED COMPENSATION */}
          {/* <Typography
            variant="subtitle2"
            sx={{ color: PRIMARY_COLOR, fontWeight: 600, mb: 1 }}
          >
            Expected Compensation
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Expected Minimum Salary"
                fullWidth
                required
                name="minExpected"
                disabled={isDisabled}
                value={formatCurrency(data.minExpected)}
                onChange={handleChange}
                sx={fieldStyles}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Expected Maximum Salary"
                fullWidth
                required
                name="maxExpected"
                disabled={isDisabled}
                value={formatCurrency(data.maxExpected)}
                onChange={handleChange}
                sx={fieldStyles}
              />
            </Grid>
          </Grid> */}

          {/* <Divider sx={{ mb: 2 }} /> */}

          {/* BILLING */}
          <Typography
            variant="subtitle2"
            sx={{ color: PRIMARY_COLOR, fontWeight: 600, mb: 1 }}
          >
            Billing Structure
          </Typography>

          <Grid container spacing={2}>
            {["perHour", "perDay", "perWeek", "perMonth"].map((field) => (
              <Grid item xs={12} md={6} key={field}>
                <TextField
                  fullWidth
                  name={field}
                  disabled={isDisabled}
                  sx={fieldStyles}
                  required={field === "perMonth"}
                  label={field.replace("per", "Per ")}
                  value={formatCurrency(data[field])}
                  onChange={handleChange}
                  InputLabelProps={{
                    required: field === "perMonth",
                    sx: { "& .MuiFormLabel-asterisk": { color: "red" } },
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {/* <Box sx={{ background: "#f2f8ff", p: 2, borderRadius: 2, my: 2 }}>
            <Typography fontWeight={600} sx={{ color: PRIMARY_COLOR }}>
              Salary Preview
            </Typography>
            <Typography>
              Hourly: <b>{formatCurrency(data.perHour)}</b>
            </Typography>
            <Typography>
              Weekly: <b>{formatCurrency(data.perWeek)}</b>
            </Typography>
            <Typography>
              Monthly: <b>{formatCurrency(data.perMonth)}</b>
            </Typography>
            <Typography>
              Annual:{" "}
              <b>
                {formatCurrency(
                  data.perMonth
                    ? Number(unformatCurrency(data.perMonth)) * 12
                    : 0
                )}
              </b>
            </Typography>
          </Box> */}

          {/*AI Recommendation */}
          {/* {suggestedSalary && (
            <Box
              sx={{
                background: "#fff6db",
                p: 2,
                borderRadius: 2,
                borderLeft: "4px solid #ffb400",
                mb: 2,
              }}
            >
              <Typography fontWeight={600} sx={{ color: "#8b6b00" }}>
                🤖 AI Market Salary Suggestion
              </Typography>
              <Typography>
                Based on skills, role & experience → Suggested:{" "}
                <b style={{ color: PRIMARY_COLOR }}>
                  {formatCurrency(suggestedSalary)}
                </b>{" "}
                / month
              </Typography>
            </Box>
          )} */}

          <Divider sx={{ my: 2 }} />

          {/* Preferences */}
          <Typography
            variant="subtitle2"
            sx={{ color: PRIMARY_COLOR, fontWeight: 600, mb: 1 }}
          >
            Work & Availability Preferences
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                required
                disabled={isDisabled}
                sx={{ mb: 2 }}
              >
                <InputLabel>Work Preference</InputLabel>
                <Select
                  label="Work Preference" // <-- Required for proper floating spacing
                  name="workPreference"
                  value={data.workPreference}
                  onChange={handleChange}
                >
                  <MenuItem value="remote">Remote</MenuItem>
                  <MenuItem value="hybrid">Hybrid</MenuItem>
                  <MenuItem value="onsite">Onsite</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                required
                disabled={isDisabled}
                sx={{ mb: 2 }}
              >
                <InputLabel>Notice Period</InputLabel>
                <Select
                  label="Notice Period" // <-- Same fix here
                  name="noticePeriod"
                  value={data.noticePeriod}
                  onChange={handleChange}
                >
                  <MenuItem value="immediate">Immediate</MenuItem>
                  <MenuItem value="15-days">15 Days</MenuItem>
                  <MenuItem value="30-days">30 Days</MenuItem>
                  <MenuItem value="60-days">60 Days</MenuItem>
                  <MenuItem value="90-days">90 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <Switch
                disabled={isDisabled}
                checked={data.openForNegotiation}
                name="openForNegotiation"
                onChange={handleChange}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#286a94",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#286a94",
                  },
                }}
              />

              <Typography sx={{ color: "#286a94", fontWeight: 500 }}>
                Open for Negotiation
              </Typography>
            </Grid>
          </Grid>

          {!isDisabled && (
            <Button
              variant="contained"
              type="submit"
              sx={{
                background: PRIMARY_COLOR,
                "&:hover": { background: PRIMARY_COLOR },
                fontSize:"16px",
                mt: 2,
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "Save Changes"
              )}
            </Button>
          )}
        </form>
      </Box>
    </>
  );
};

export default Pricing;
